'use client';

import {
  EmojiPicker as EmojiPickerPrimitive,
  type EmojiPickerListCategoryHeaderProps,
  type EmojiPickerListEmojiProps,
  type EmojiPickerListRowProps,
} from 'frimousse';
import { LoaderIcon, SearchIcon } from 'lucide-react';
import type { ComponentProps } from 'react';
import * as React from 'react';

import { cn } from '~/lib/utils';

function EmojiPicker({ className, ...props }: ComponentProps<typeof EmojiPickerPrimitive.Root>) {
  return (
    <EmojiPickerPrimitive.Root
      className={cn(
        'isolate flex h-full w-fit max-w-[min(100vw-1rem,20rem)] flex-col overflow-hidden rounded-lg border border-[#405820]/25 bg-[#f4f6ea] text-gray-900 shadow-md dark:border-[#405820]/40 dark:bg-neutral-900 dark:text-neutral-100',
        className,
      )}
      data-slot="emoji-picker"
      {...props}
    />
  );
}

function EmojiPickerSearch({
  className,
  placeholder,
  ...props
}: ComponentProps<typeof EmojiPickerPrimitive.Search> & {
  placeholder?: string;
}) {
  return (
    <div
      className={cn(
        'flex h-9 shrink-0 items-center gap-2 border-b border-[#405820]/15 bg-[#e8edc8]/80 px-3 dark:border-white/10 dark:bg-neutral-800/50',
        className,
      )}
      data-slot="emoji-picker-search-wrapper"
    >
      <SearchIcon className="size-4 shrink-0 opacity-50" aria-hidden />
      <EmojiPickerPrimitive.Search
        placeholder={placeholder}
        className="flex h-10 w-full appearance-none rounded-none border-0 bg-transparent py-3 text-sm shadow-none ring-0 ring-offset-0 outline-none placeholder:text-slate-500 focus:border-0 focus:shadow-none focus:ring-0 focus:ring-offset-0 focus:outline-none focus-visible:border-0 focus-visible:shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 dark:placeholder:text-neutral-500"
        data-slot="emoji-picker-search"
        {...props}
      />
    </div>
  );
}

function EmojiPickerRow({ children, ...props }: EmojiPickerListRowProps) {
  return (
    <div
      {...props}
      className={cn('scroll-my-1 px-1', props.className)}
      data-slot="emoji-picker-row"
    >
      {children}
    </div>
  );
}

function EmojiPickerEmoji({ emoji, className, ...props }: EmojiPickerListEmojiProps) {
  return (
    <button
      {...props}
      type="button"
      className={cn(
        'flex size-7 items-center justify-center rounded-sm text-base data-[active]:bg-[#9ca965]/50 dark:data-[active]:bg-neutral-700',
        className,
      )}
      data-slot="emoji-picker-emoji"
    >
      {emoji.emoji}
    </button>
  );
}

function EmojiPickerCategoryHeader({ category, ...props }: EmojiPickerListCategoryHeaderProps) {
  return (
    <div
      {...props}
      className={cn(
        'bg-[#f4f6ea] px-3 pt-3.5 pb-2 text-xs leading-none text-slate-600 dark:bg-neutral-900 dark:text-neutral-400',
        props.className,
      )}
      data-slot="emoji-picker-category-header"
    >
      {category.label}
    </div>
  );
}

function EmojiPickerContent({
  className,
  emptyLabel,
  ...props
}: ComponentProps<typeof EmojiPickerPrimitive.Viewport> & {
  emptyLabel?: string;
}) {
  return (
    <EmojiPickerPrimitive.Viewport
      className={cn('relative min-h-0 flex-1 outline-none', className)}
      data-slot="emoji-picker-viewport"
      {...props}
    >
      <EmojiPickerPrimitive.Loading
        className="absolute inset-0 flex items-center justify-center text-slate-500 dark:text-neutral-400"
        data-slot="emoji-picker-loading"
      >
        <LoaderIcon className="size-4 animate-spin" aria-hidden />
      </EmojiPickerPrimitive.Loading>
      <EmojiPickerPrimitive.Empty
        className="absolute inset-0 flex items-center justify-center text-sm text-slate-500 dark:text-neutral-400"
        data-slot="emoji-picker-empty"
      >
        {emptyLabel ?? 'No emoji found.'}
      </EmojiPickerPrimitive.Empty>
      <EmojiPickerPrimitive.List
        className="pb-1 select-none"
        components={{
          Row: EmojiPickerRow,
          Emoji: EmojiPickerEmoji,
          CategoryHeader: EmojiPickerCategoryHeader,
        }}
        data-slot="emoji-picker-list"
      />
    </EmojiPickerPrimitive.Viewport>
  );
}

function EmojiPickerFooter({
  className,
  selectHint,
  skinToneAriaLabel,
  ...props
}: ComponentProps<'div'> & {
  selectHint?: string;
  skinToneAriaLabel?: string;
}) {
  return (
    <div
      className={cn(
        'flex w-full max-w-(--frimousse-viewport-width) min-w-0 shrink-0 items-center gap-2 border-t border-[#405820]/15 bg-[#eef2d8]/90 p-2 dark:border-white/10 dark:bg-neutral-800/60',
        className,
      )}
      data-slot="emoji-picker-footer"
      {...props}
    >
      <EmojiPickerPrimitive.ActiveEmoji>
        {({ emoji }) =>
          emoji ? (
            <>
              <div className="flex size-7 flex-none items-center justify-center text-lg">
                {emoji.emoji}
              </div>
              <span className="min-w-0 flex-1 truncate text-xs text-slate-700 dark:text-neutral-300">
                {emoji.label}
              </span>
            </>
          ) : (
            <span className="ml-1.5 flex h-7 min-w-0 flex-1 items-center truncate text-xs text-slate-500 dark:text-neutral-500">
              {selectHint ?? 'Select an emoji…'}
            </span>
          )
        }
      </EmojiPickerPrimitive.ActiveEmoji>
      <EmojiPickerPrimitive.SkinToneSelector
        type="button"
        className="ml-auto flex size-8 shrink-0 items-center justify-center rounded-md text-lg hover:bg-black/5 dark:hover:bg-white/10"
        aria-label={skinToneAriaLabel ?? 'Skin tone'}
      />
    </div>
  );
}

export {
  EmojiPicker,
  EmojiPickerContent,
  EmojiPickerFooter,
  EmojiPickerPrimitive,
  EmojiPickerSearch,
};
